import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@material-ui/core";
import { isMobile } from "react-device-detect";
import { usePostHog } from "posthog-js/react";
import { useSelector } from "react-redux";
import styles from "./MyWashAppPromoModal.module.scss";
import { CloseIcon } from "../../../assets";
import {
  shouldShow,
  markShown,
  incrementDismiss,
  markPermanentlyDismissed,
  getDismissCount,
} from "../../../utils/mywashPromoDismissal";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=rs.mywash";
const APP_STORE_URL = "https://apps.apple.com/app/id6758160311";
const WEBSITE_URL = "https://mywash.rs";
const REGISTER_URL = "https://app.mywash.rs/partner-registration";
const LOGO_SRC = "/images/mywash-promo/mywash-logo.png";
const SWIPE_THRESHOLD_PX = 50;

const slides = [
  {
    key: "tokens-in-phone",
    title: "Žetoni u vašem telefonu",
    description:
      "Vaši klijenti nose žetone u džepu — spremni za pranje u svakom trenutku.",
    image: "/images/mywash-promo/homescreen-white.png",
  },
  {
    key: "qr-activation",
    variant: "card",
    title: "Aktivacija skeniranjem QR koda",
    description:
      "Klijent skenira QR kod na boksu — kroz MyWash aplikaciju ili kamerom telefona. Žeton se pušta i pranje se aktivira odmah. Bez kovanica, bez čekanja: žeton → pranje → bodovi lojalnosti.",
    image: "/images/mywash-promo/mywash-qr.jpg",
  },
  {
    key: "buy-tokens-rebate",
    title: "Kupovina žetona uz rabate po korisniku",
    description:
      "Podesite individualne popuste i rabate za svakog korisnika — \"kupi 10, dobij 2 gratis\".",
    image: "/images/mywash-promo/dopuna.png",
  },
  {
    key: "buy-tokens-invoice",
    title: "Kupovina žetona preko faktura",
    description:
      "Partnerske firme kupuju žetone uz fakturu i odloženo plaćanje — profesionalan B2B odnos sa automatskom evidencijom.",
    image: "/images/mywash-promo/fiscal-invoice.png",
  },
  {
    key: "partner-accounts",
    title: "Nalozi za partnerske firme",
    description:
      "Firme kupuju žetone preko faktura i distribuiraju ih svojim radnicima — svaki radnik ima svoj nalog sa kontrolom i evidencijom potrošnje. Idealno za firme sa kojima već imate redovnu saradnju.",
    image: "/images/mywash-promo/homescreen-white.png",
  },
  {
    key: "topup-anytime",
    title: "Dopuna naloga u svakom momentu",
    description:
      "Klijenti i firme dopunjavaju nalog kad god žele, gde god žele. Moguća dopuna i preko postojećih CardPay terminala.",
    image: "/images/mywash-promo/dopuna-await-payment.png",
  },
  {
    key: "promotions",
    title: "Promocije",
    description:
      "Pokrenite kampanje i šaljite obaveštenja vašim korisnicima o popustima — motivacija za češće pranje i bolje zadržavanje korisnika.",
    image: "/images/mywash-promo/notifications.png",
  },
  {
    key: "vouchers",
    title: "Vaučeri",
    description:
      "Kreirajte poklon vaučere — idealno za promociju perionice preko firmi poput registracije i tehničkih pregleda.",
    image: "/images/mywash-promo/my-qr-code.png",
  },
  {
    key: "box-time-release",
    title: "Puštanje vremena po boksovima",
    description:
      "Aktivirajte vreme pranja direktno na izabranom boksu — bez keša i bez kovanica.",
    image: "/images/mywash-promo/location-preview.png",
  },
  {
    key: "card-pay-topup",
    title: "Puštanje i dopuna žetona na card-pay terminalima",
    description:
      "Na svakom card-pay terminalu klijent može da dopuni žetone ili pokrene pranje.",
    image: "/images/mywash-promo/sublocation-dopuna-vs-redeem.png",
  },
];

const MyWashAppPromoModal = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.id;
  const posthog = usePostHog();

  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewedSlides, setViewedSlides] = useState(() => new Set([0]));
  const openedAtRef = useRef(null);
  const autoStartedRef = useRef(false);
  const swipeStartXRef = useRef(null);

  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!userId) return;
    if (!shouldShow(userId)) return;

    autoStartedRef.current = true;
    openModal("auto");
    markShown(userId);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (source) => {
    setIsOpen(true);
    setCurrentSlide(0);
    setViewedSlides(new Set([0]));
    openedAtRef.current = Date.now();
    posthog?.capture("mywash_promo_opened", {
      source,
      device_type: isMobile ? "mobile" : "desktop",
      dismiss_count_before: userId ? getDismissCount(userId) : null,
      timestamp: new Date().toISOString(),
    });
  };

  const closeWithDismiss = (source) => {
    if (userId) incrementDismiss(userId);
    const durationSec = openedAtRef.current
      ? Math.round((Date.now() - openedAtRef.current) / 1000)
      : null;
    posthog?.capture("mywash_promo_dismissed", {
      source,
      duration_seconds: durationSec,
      slides_viewed: viewedSlides.size,
      total_slides: slides.length,
      final_slide: currentSlide,
      dismiss_count_after: userId ? getDismissCount(userId) : null,
    });
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    if (userId) markPermanentlyDismissed(userId);
    posthog?.capture("mywash_promo_dont_show_again", {
      slides_viewed: viewedSlides.size,
      final_slide: currentSlide,
    });
    setIsOpen(false);
  };

  const handleStoreClick = (storeName, url) => {
    posthog?.capture("mywash_promo_store_clicked", {
      store: storeName,
      current_slide: currentSlide,
      slide_key: slides[currentSlide].key,
      slides_viewed: viewedSlides.size,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const handleWebsiteClick = () => {
    posthog?.capture("mywash_promo_website_clicked", {
      current_slide: currentSlide,
      slide_key: slides[currentSlide].key,
      slides_viewed: viewedSlides.size,
    });
    window.open(WEBSITE_URL, "_blank", "noopener,noreferrer");
  };

  // Build the MyWash partner-registration URL, prefilled with the logged-in
  // owner's company + contact data so the form opens ready to submit.
  const buildRegisterUrl = () => {
    const company = user?.companies?.[0];
    const contactPerson = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
    const params = new URLSearchParams();
    if (company?.name) params.set("companyName", company.name);
    if (company?.PIB) params.set("pib", company.PIB);
    if (contactPerson) params.set("contactPerson", contactPerson);
    if (user?.email) params.set("email", user.email);
    if (user?.phone) params.set("phone", user.phone);
    const qs = params.toString();
    return qs ? `${REGISTER_URL}?${qs}` : REGISTER_URL;
  };

  const handleRegisterClick = (source) => {
    posthog?.capture("mywash_promo_register_clicked", {
      source,
      current_slide: currentSlide,
      slide_key: slides[currentSlide]?.key,
      slides_viewed: viewedSlides.size,
      device_type: isMobile ? "mobile" : "desktop",
    });
    window.open(buildRegisterUrl(), "_blank", "noopener,noreferrer");
  };

  const goToSlide = (index, navType) => {
    const previous = currentSlide;
    setCurrentSlide(index);
    setViewedSlides((seen) => new Set([...seen, index]));
    posthog?.capture("mywash_promo_slide_viewed", {
      from_slide: previous,
      to_slide: index,
      slide_key: slides[index].key,
      navigation_type: navType,
    });
  };

  const prevSlide = () => {
    const next = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    goToSlide(next, "prev_button");
  };

  const nextSlide = () => {
    const next = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(next, "next_button");
  };

  const handlePointerDown = (e) => {
    swipeStartXRef.current = e.clientX;
  };

  const handlePointerUp = (e) => {
    if (swipeStartXRef.current == null) return;
    const delta = e.clientX - swipeStartXRef.current;
    swipeStartXRef.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta < 0) {
      const next =
        currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
      goToSlide(next, "swipe_left");
    } else {
      const next =
        currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
      goToSlide(next, "swipe_right");
    }
  };

  const handlePointerCancel = () => {
    swipeStartXRef.current = null;
  };

  return (
    <>
      <div
        className={styles.bannerTile}
        role="button"
        tabIndex={0}
        onClick={() => openModal("banner_click")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal("banner_click");
          }
        }}
      >
        <img src={LOGO_SRC} alt="MyWash" className={styles.bannerLogo} />
        <div className={styles.bannerText}>
          <div className={styles.bannerTitle}>
            <span className={styles.bannerFreeBadge}>BESPLATNO</span>
            Besplatna aplikacija za sve naše korisnike
          </div>
          <div className={styles.bannerSubtitle}>
            Za puštanje žetona i aktivaciju pranja po boksovima
          </div>
          <div className={styles.bannerChips}>
            <span className={styles.bannerChip}>Bez sitnog novca</span>
            <span className={styles.bannerChip}>Dopuna preko aplikacije</span>
            <span className={styles.bannerChip}>Program lojalnosti</span>
          </div>
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.bannerRegisterBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleRegisterClick("banner_tile");
              }}
            >
              Registrujte se besplatno
            </button>
            <div className={styles.bannerCta}>
              Saznajte više <span className={styles.bannerArrow}>→</span>
            </div>
          </div>
        </div>
      </div>

      <Modal open={isOpen} onClose={() => closeWithDismiss("backdrop_or_esc")}>
        <div className={styles.modalWrap}>
          <div className={styles.modalInner}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <img
                  src={LOGO_SRC}
                  alt="MyWash"
                  className={styles.modalLogo}
                />
                <h2 className={styles.modalTitle}>
                  <span className={styles.modalTitleRow}>
                    MyWash aplikacija
                    <span className={styles.modalFreeBadge}>BESPLATNO</span>
                  </span>
                  <span className={styles.modalSubtitle}>
                    Besplatna aplikacija za sve naše korisnike
                  </span>
                </h2>
              </div>
              <div
                className={styles.closeIconWrap}
                onClick={() => closeWithDismiss("close_button")}
                role="button"
                aria-label="Zatvori"
              >
                <img src={CloseIcon} alt="Zatvori" className={styles.closeIcon} />
              </div>
            </div>

            <div className={styles.modalBody}>
              <div
                className={styles.viewport}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onPointerLeave={handlePointerCancel}
              >
                <div
                  className={styles.track}
                  style={{
                    transform: `translate3d(-${currentSlide * 100}%, 0, 0)`,
                  }}
                >
                  {slides.map((s) => (
                    <div key={s.key} className={styles.slide}>
                      {s.variant === "card" ? (
                        <div
                          className={styles.cardFrame}
                          style={{ backgroundImage: `url(${s.image})` }}
                          role="img"
                          aria-label={s.title}
                        />
                      ) : (
                        <div className={styles.phoneFrame}>
                          <img
                            src={s.image}
                            alt={s.title}
                            className={styles.slideImage}
                            draggable={false}
                          />
                        </div>
                      )}
                      <div className={styles.slideCopy}>
                        <h3 className={styles.slideTitle}>{s.title}</h3>
                        <p className={styles.slideDescription}>
                          {s.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.navRow}>
                <button
                  type="button"
                  className={styles.navArrow}
                  onClick={prevSlide}
                  aria-label="Prethodno"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <div className={styles.dots}>
                  {slides.map((s, index) => (
                    <button
                      key={s.key}
                      type="button"
                      className={`${styles.dot} ${
                        index === currentSlide ? styles.dotActive : ""
                      }`}
                      onClick={() => goToSlide(index, "dot")}
                      aria-label={`Slajd ${index + 1}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.navArrow}
                  onClick={nextSlide}
                  aria-label="Sledeće"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.registerCta}
                onClick={() => handleRegisterClick("modal")}
              >
                Registrujte se besplatno
              </button>
              <div className={styles.storeButtons}>
                <button
                  type="button"
                  className={`${styles.storeButton} ${styles.appStore}`}
                  onClick={() => handleStoreClick("app_store", APP_STORE_URL)}
                  aria-label="Preuzmi na App Store"
                >
                  <img
                    src="/images/mywash-promo/app-store-badge.svg"
                    alt="App Store"
                    className={styles.storeBadge}
                  />
                </button>
                <button
                  type="button"
                  className={`${styles.storeButton} ${styles.playStore}`}
                  onClick={() => handleStoreClick("google_play", PLAY_STORE_URL)}
                  aria-label="Preuzmi na Google Play"
                >
                  <img
                    src="/images/mywash-promo/google-play-badge.png"
                    alt="Google Play"
                    className={styles.storeBadge}
                  />
                </button>
              </div>
              <div className={styles.footerLinks}>
                <button
                  type="button"
                  className={styles.websiteLink}
                  onClick={handleWebsiteClick}
                >
                  mywash.rs
                  <span className={styles.externalArrow} aria-hidden="true">
                    ↗
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.dontShowAgain}
                  onClick={handleDontShowAgain}
                >
                  Ne prikazuj više
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MyWashAppPromoModal;
